/**
 * Chọn mẫu ZNS của Zalo OA đã kết nối và ghép dữ liệu vào từng tham số của mẫu.
 * Danh sách mẫu + chi tiết (tham số, nút, giá, link xem trước) lấy thật từ Zalo qua
 * /automation-v2/zns/templates. Giá trị lưu dạng "tên_tham_số=giá trị" mỗi dòng
 * (engine thay {{biến}} lúc gửi).
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react'
import { api, apiError } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface MauZns { templateId: string; templateName: string; status: string; templateType?: string | null; previewUrl?: string | null; soThamSo: number }
interface ThamSoZns { name: string; require?: boolean; type?: string; maxLength?: number; minLength?: number; acceptNull?: boolean }
interface ChiTietZns {
  oa: { id: string; name: string }
  templateId: string; templateName: string; status: string; templateType?: string
  params: ThamSoZns[]; previewUrl?: string; price?: number; templateQuality?: string; timeout?: number
  buttons?: Array<{ title?: string; content?: string }>
}

const TRANG_THAI: Record<string, { ten: string; mau: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  ENABLE: { ten: 'Đã duyệt', mau: 'success' },
  PENDING_REVIEW: { ten: 'Chờ duyệt', mau: 'warning' },
  REJECT: { ten: 'Bị từ chối', mau: 'destructive' },
  DISABLE: { ten: 'Đã tắt', mau: 'secondary' },
}

/** Biến gợi ý cho ô tham số. */
const BIEN = [
  ['customer.name', 'Tên khách'], ['customer.phone', 'SĐT khách'], ['customer.code', 'Mã khách'],
  ['order.code', 'Mã đơn / hoá đơn'], ['order.total', 'Tổng tiền'], ['order.date', 'Ngày mua'], ['order.status', 'Trạng thái đơn'],
  ['order.items[0].name', 'Tên SP đầu tiên'], ['order.branch', 'Chi nhánh'], ['voucher.code', 'Mã voucher'], ['voucher.giaTri', 'Giá trị voucher'],
] as const

/** Đoán biến theo tên tham số của mẫu (customer_name → {{customer.name}}…). */
export function doanBien(ten: string): string {
  const t = ten.toLowerCase()
  if (/(customer|khach|ten).*(name|ten)|^name$|^ten$|ho_ten|fullname/.test(t)) return '{{customer.name}}'
  if (/phone|sdt|so_dien/.test(t)) return '{{customer.phone}}'
  if (/order|don|invoice|hoa_don|ma_don/.test(t) && /code|id|ma|so/.test(t)) return '{{order.code}}'
  if (/price|total|amount|tien|gia_tri|thanh_toan/.test(t)) return '{{order.total}}'
  if (/date|ngay|time|thoi_gian/.test(t)) return '{{order.date}}'
  if (/product|san_pham|item|ten_sp/.test(t)) return '{{order.items[0].name}}'
  if (/status|trang_thai/.test(t)) return '{{order.status}}'
  if (/voucher|coupon|ma_giam|khuyen_mai/.test(t)) return '{{voucher.code}}'
  if (/customer_code|ma_khach|member/.test(t)) return '{{customer.code}}'
  return ''
}

export function docThamSo(s: string): Record<string, string> {
  return Object.fromEntries(s.split('\n').filter((l) => l.includes('=')).map((l) => { const i = l.indexOf('='); return [l.slice(0, i).trim(), l.slice(i + 1).trim()] }))
}
const ghiThamSo = (o: Record<string, string>) => Object.entries(o).map(([k, v]) => `${k}=${v}`).join('\n')

export function ChonMauZns({ templateId, params, onChange }: { templateId: string; params: string; onChange: (v: { templateId: string; params: string }) => void }) {
  const [ds, setDs] = useState<MauZns[] | null>(null)
  const [oa, setOa] = useState<{ id: string; name: string } | null>(null)
  const [thongBao, setThongBao] = useState<string>()
  const [canhBao, setCanhBao] = useState<{ maLoi: string; tomTat: string; cachXuLy: string } | null>(null)
  const [appId, setAppId] = useState<string | null>(null)
  const [dangTai, setDangTai] = useState(false)
  const [ct, setCt] = useState<ChiTietZns | null>(null)
  const [loiCt, setLoiCt] = useState<string>()
  const giaTri = useMemo(() => docThamSo(params), [params])

  const taiDs = async (lamMoi = false) => {
    setDangTai(true)
    try {
      const { data } = await api.get<{ oa: { id: string; name: string } | null; templates: MauZns[]; thongBao?: string; appId?: string | null; canhBaoThietLap?: { maLoi: string; tomTat: string; cachXuLy: string } }>(`/automation-v2/zns/templates${lamMoi ? '?refresh=1' : ''}`)
      setDs(data.templates); setOa(data.oa); setThongBao(data.thongBao); setCanhBao(data.canhBaoThietLap ?? null); setAppId(data.appId ?? null)
    } catch (err) { setDs([]); setThongBao(apiError(err)) } finally { setDangTai(false) }
  }
  useEffect(() => { void taiDs() }, [])

  useEffect(() => {
    if (!templateId) { setCt(null); return }
    let huy = false
    setLoiCt(undefined)
    api.get<ChiTietZns>(`/automation-v2/zns/templates/${encodeURIComponent(templateId)}`)
      .then(({ data }) => {
        if (huy) return
        setCt(data)
        // Mẫu mới chọn: điền sẵn biến đoán theo tên tham số, giữ giá trị người dùng đã nhập.
        const hien = docThamSo(params)
        const moi: Record<string, string> = {}
        for (const p of data.params) moi[p.name] = hien[p.name] ?? doanBien(p.name)
        const chuoi = ghiThamSo(moi)
        if (chuoi !== params) onChange({ templateId, params: chuoi })
      })
      .catch((err) => { if (!huy) { setCt(null); setLoiCt(apiError(err)) } })
    return () => { huy = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ tải lại khi đổi mẫu
  }, [templateId])

  const sua = (ten: string, v: string) => onChange({ templateId, params: ghiThamSo({ ...giaTri, [ten]: v }) })
  const mauChon = ds?.find((t) => t.templateId === templateId)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">Mẫu ZNS {oa && <span className="font-normal text-muted-foreground">· OA {oa.name}</span>}</Label>
        <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" disabled={dangTai} onClick={() => void taiDs(true)}>
          <RefreshCw className={cn('h-3 w-3', dangTai && 'animate-spin')} /> Lấy lại từ Zalo
        </Button>
      </div>
      <Select value={templateId || undefined} onValueChange={(v) => onChange({ templateId: v, params })}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={ds === null ? 'Đang tải mẫu…' : ds.length ? 'Chọn mẫu ZNS' : 'OA chưa có mẫu ZNS'} /></SelectTrigger>
        <SelectContent>
          {(ds ?? []).map((t) => (
            <SelectItem key={t.templateId} value={t.templateId} disabled={t.status !== 'ENABLE'}>
              {t.templateName} <span className="text-muted-foreground">· #{t.templateId}{t.status !== 'ENABLE' ? ` · ${TRANG_THAI[t.status]?.ten ?? t.status}` : ''}</span>
            </SelectItem>
          ))}
          {templateId && !mauChon && <SelectItem value={templateId}>#{templateId} (không có trong danh sách)</SelectItem>}
        </SelectContent>
      </Select>
      {thongBao && <p className="text-[11px] text-warning">{thongBao}</p>}
      {canhBao && (
        <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-[11px]">
          <p className="flex items-center gap-1 font-medium text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> Lần gửi gần nhất lỗi {canhBao.maLoi}: {canhBao.tomTat}</p>
          <p className="text-muted-foreground">{canhBao.cachXuLy}</p>
          {appId && <p className="text-muted-foreground">App ID của nền tảng: <code className="select-all rounded bg-muted px-1">{appId}</code></p>}
        </div>
      )}
      {loiCt && <p className="text-[11px] text-destructive">Không lấy được chi tiết mẫu: {loiCt}</p>}

      {ct && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-2">
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <Badge variant={TRANG_THAI[ct.status]?.mau ?? 'secondary'}>{TRANG_THAI[ct.status]?.ten ?? ct.status}</Badge>
            {ct.templateType && <Badge variant="secondary">{ct.templateType}</Badge>}
            {ct.price != null && <span className="text-muted-foreground">{ct.price.toLocaleString('vi-VN')}đ / tin</span>}
            {ct.previewUrl && <a href={ct.previewUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-primary hover:underline">Xem mẫu <ExternalLink className="h-3 w-3" /></a>}
          </div>
          {ct.params.length === 0 && <p className="text-[11px] text-muted-foreground">Mẫu không có tham số.</p>}
          {ct.params.map((p) => {
            const v = giaTri[p.name] ?? ''
            const thieu = p.require && !v.trim()
            const coBien = v.includes('{{')
            const dai = !coBien && p.maxLength != null && v.length > p.maxLength
            return (
              <div key={p.name}>
                <Label className="flex items-center gap-1 font-mono text-[11px]">
                  {p.name}{p.require && <span className="text-destructive">*</span>}
                  <span className="font-sans font-normal text-muted-foreground">{p.type ?? ''}{p.maxLength ? ` · tối đa ${p.maxLength} ký tự` : ''}</span>
                </Label>
                <Input list="zns-bien" value={v} onChange={(e) => sua(p.name, e.target.value)} className={cn('h-7 font-mono text-[11px]', (thieu || dai) && 'border-destructive')} placeholder="{{customer.name}} hoặc chữ cố định" />
                {thieu && <p className="text-[10.5px] text-destructive">Tham số bắt buộc — Zalo sẽ từ chối nếu để trống</p>}
                {dai && <p className="text-[10.5px] text-destructive">Dài hơn {p.maxLength} ký tự</p>}
              </div>
            )
          })}
          {!!ct.buttons?.length && <p className="text-[10.5px] text-muted-foreground">Nút: {ct.buttons.map((b) => b.title).filter(Boolean).join(' · ')}</p>}
          <datalist id="zns-bien">{BIEN.map(([b, ten]) => <option key={b} value={`{{${b}}}`}>{ten}</option>)}</datalist>
        </div>
      )}
    </div>
  )
}
